import {useEffect,useState} from 'react';
import {Permissions as P} from '@myfm/domain';
import {api} from '../api';
import {formatDateOnly,formatDateTime,timestampDateOnlyValue} from '../dates';
import {useWorkspaceSession} from '../ui/StaffSession';
import {DataTable} from '../ui/DataTable';
import {SummaryStrip} from '../ui/SummaryStrip';
import {StatusBadge} from '../ui/StatusBadge';
import {ErrorState,Loading} from '../ui/State';
import {Link} from '../ui/ScopedLink';

type Worker={id:string;personName:string;reference:string};
type Facts={intervals:{id:string;kind:string;starts_at:string;status:string}[];
 requirements:{id:string;title:string;effective_from:string;effective_to:string|null;reminder_on:string;retired:boolean}[]};
type Row={id:string;worker:Worker;title:string;due:string|null;status:string;kind:'Leave'|'Requirement'|'Unavailable'};
type Result={key:string;rows:Row[];known:number;loaded:string};

/** D24 uses the private HR readers. Missing policy/monthly outcome sources stay unknown. */
export function WorkforceOverview(){
 const session=useWorkspaceSession()!,allowed=session.capabilities.includes(P.ViewWorkforce);
 const [directory,setDirectory]=useState<Worker[]>(),[error,setError]=useState<unknown>(),[revision,setRevision]=useState(0),[page,setPage]=useState(1);
 const [result,setResult]=useState<Result>();
 const today=timestampDateOnlyValue(new Date());
 useEffect(()=>{
  const controller=new AbortController();setDirectory(undefined);setError(undefined);
  if(allowed)void api<{workers:Worker[]}>('/api/workforce',{signal:controller.signal}).then(data=>{
   if(!Array.isArray(data.workers)||data.workers.some(worker=>!worker.id||!worker.personName)||new Set(data.workers.map(worker=>worker.id)).size!==data.workers.length)throw Error('The private workforce directory could not be verified.');
   if(!controller.signal.aborted)setDirectory(data.workers);
  }).catch(cause=>{if(!controller.signal.aborted)setError(cause)});
  return()=>controller.abort();
 },[allowed,revision]);
 const pages=Math.max(1,Math.ceil((directory?.length??0)/18)),current=Math.min(page,pages);
 const workers=directory?.slice((current-1)*18,current*18)??[];
 const key=JSON.stringify([allowed,revision,current,today,workers.map(worker=>worker.id)]);
 useEffect(()=>{
  const controller=new AbortController();
  if(allowed&&directory)void(async()=>{
   const rows:Row[]=[];let known=0;
   // Bound the existing per-person readers; never fetch or display private notes.
   for(let start=0;start<workers.length;start+=3){
    const batch=await Promise.all(workers.slice(start,start+3).map(async worker=>{
     try{
      const facts=await api<Facts>(`/api/workforce/${worker.id}/availability`,{signal:controller.signal});
      if(!Array.isArray(facts.intervals)||!Array.isArray(facts.requirements))throw Error('Invalid private HR source');
      return {known:true,rows:[
       ...facts.intervals.filter(interval=>interval.kind==='Leave'&&interval.status==='Pending').map(interval=>({
        id:'leave:'+interval.id,worker,title:'Leave request',due:interval.starts_at,status:'Pending',kind:'Leave' as const})),
       ...facts.requirements.filter(requirement=>!requirement.retired&&requirement.effective_from.slice(0,10)<=today&&(!requirement.effective_to||requirement.effective_to.slice(0,10)>=today)).map(requirement=>({
        id:'requirement:'+requirement.id,worker,title:requirement.title,due:requirement.reminder_on,
        status:requirement.reminder_on&&requirement.reminder_on.slice(0,10)<=today?'Review due':'Recorded requirement',kind:'Requirement' as const})),
      ]};
     }catch{return {known:false,rows:[{id:'unavailable:'+worker.id,worker,title:'HR review source',due:null,status:'Unavailable',kind:'Unavailable' as const}]};}
    }));
    if(controller.signal.aborted)return;
    for(const item of batch){known+=Number(item.known);rows.push(...item.rows)}
   }
   if(!controller.signal.aborted)setResult({key,rows,known,loaded:new Date().toISOString()});
  })();
  return()=>controller.abort();
 },[key,Boolean(directory)]);
 if(!allowed)return null;
 if(error)return <ErrorState error={error} onRetry={()=>setRevision(value=>value+1)}/>;
 if(!directory)return <Loading/>;
 const settled=result?.key===key,rows=settled?result.rows:[],partial=settled&&result.known<workers.length;
 const total=(kind:Row['kind'])=>!settled?'Loading…':result.known===0&&workers.length?'Unknown':
  rows.filter(row=>row.kind===kind&&(kind!=='Requirement'||row.status==='Review due')).length.toLocaleString('en-GB')+(partial?'+':'');
 return <section aria-label="Private HR review overview">
  <div className="section-heading"><h2>People &amp; HR reviews</h2><div className="button-row">
   <button className="secondary" type="button" onClick={()=>setRevision(value=>value+1)}>Refresh HR reviews</button>
   <Link to="/workforce">People directory</Link>
  </div></div>
  <SummaryStrip mode="metric" ariaLabel="Private HR work on this people page" items={[
   {label:'Pending leave requests',value:total('Leave')},{label:'Requirements due for review',value:total('Requirement')},
   {label:'Policy acknowledgments',state:'unavailable',unavailableLabel:'Unknown'},{label:'Completed this month',state:'unavailable',unavailableLabel:'Unknown'},
  ]}/>
  <p className="workspace-source-note">{settled?`View loaded ${formatDateTime(result.loaded)}. `:''}Private HR records are organisation-wide, independently of building selection.
   Counts cover these {workers.length} people from the first 200 directory results. A + marks partial coverage. Policy acknowledgments and monthly completed-work totals have no connected source here and remain Unknown.</p>
  {!settled?<Loading/>:<><DataTable caption={<span className="sr-only">Pending leave and active competence requirements, with links to their private original records</span>}
   scrollLabel="Private HR review work" rows={rows} getRowKey={row=>row.id} pageSize={18}
   rowHeader={{id:'work',header:'Work item',render:row=>row.title}} columns={[
    {id:'person',header:'Person',render:row=><Link to={'/workforce/'+row.worker.id}>{row.worker.personName}</Link>},
    {id:'owner',header:'Review owner',render:()=> 'Not recorded'},
    {id:'due',header:'Due / leave starts',render:row=>formatDateOnly(row.due,'Not recorded')},
    {id:'status',header:'State',render:row=><StatusBadge text={row.status} tone={row.status==='Unavailable'?'unavailable':row.status==='Review due'?'warning':'information'}/>},
    {id:'next',header:'Next action',render:row=><Link to={'/workforce/'+row.worker.id+'/availability'}>Review private record</Link>},
   ]}/>{!rows.length&&<p className="empty">No pending leave or active requirements were returned for these people. This does not certify training, policy acknowledgment or staffing readiness.</p>}</>}
  {pages>1&&<nav className="pagination" aria-label="HR people pages"><button type="button" className="secondary" disabled={current===1} onClick={()=>setPage(current-1)}>Previous people</button><span>{current} of {pages}</span><button type="button" className="secondary" disabled={current===pages} onClick={()=>setPage(current+1)}>Next people</button></nav>}
 </section>;
}
